import { create } from 'zustand';
import type { AppState, HttpMethod, ParsedSpec, Endpoint, SearchMatch } from './types';

const initialFilters = {
  methods: [] as HttpMethod[],
  specs: [] as string[],
  tags: [] as string[],
};

export const useStore = create<AppState>((set, get) => ({
  // Initial state
  specs: [],
  endpoints: [],
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  filters: initialFilters,
  activeMatches: new Map<string, SearchMatch[]>(),
  viewMode: 'search',
  theme: 'dark',
  selectedEndpoint: null,
  expandedGroups: new Set(['methods', 'specs', 'tags']),
  collapsedSchemas: new Set(),

  // Actions
  addSpec: (spec: ParsedSpec) => {
    set((state) => ({
      specs: [...state.specs, spec],
      endpoints: [...state.endpoints, ...spec.endpoints],
    }));
  },

  removeSpec: (id: string) => {
    set((state) => ({
      specs: state.specs.filter((s) => s.id !== id),
      endpoints: state.endpoints.filter((e) => e.specId !== id),
    }));
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query, isSearching: query.length > 0 });
  },

  setSearchResults: (results: Endpoint[]) => {
    set({ searchResults: results });
  },

  setActiveMatches: (matches: Map<string, SearchMatch[]>) => {
    set({ activeMatches: matches });
  },

  setViewMode: (mode) => set({ viewMode: mode }),

  setTheme: (theme) => {
    set({ theme });
    applyTheme(theme);
  },

  setSelectedEndpoint: (endpoint) => set({ selectedEndpoint: endpoint }),

  toggleGroup: (groupId: string) => {
    set((state) => {
      const newExpanded = new Set(state.expandedGroups);
      if (newExpanded.has(groupId)) {
        newExpanded.delete(groupId);
      } else {
        newExpanded.add(groupId);
      }
      return { expandedGroups: newExpanded };
    });
  },

  toggleSchema: (schemaId: string) => {
    set((state) => {
      const newCollapsed = new Set(state.collapsedSchemas);
      if (newCollapsed.has(schemaId)) {
        newCollapsed.delete(schemaId);
      } else {
        newCollapsed.add(schemaId);
      }
      return { collapsedSchemas: newCollapsed };
    });
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }));
  },

  clearFilters: () => set({ filters: initialFilters }),
}));

function applyTheme(theme: 'dark' | 'light' | 'system') {
  const root = document.documentElement;
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  if (isDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// Initialize theme
if (typeof window !== 'undefined') {
  const savedTheme = localStorage.getItem('openapi-gps-theme') as 'dark' | 'light' | 'system' | null;
  if (savedTheme) {
    applyTheme(savedTheme);
  }
}
